import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { DialogHeaderComponent, LoaderComponent } from '@mode/shared/ui';
import { CompiledField } from '@mode/shared/util-switch';
import { Inject } from '@angular/core';
import { ButtonComponent, IconComponent } from '@mode/capra';
import { ChartTypes, BinSizeTypes, FlamingoResponse } from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS, getFormatterByType } from '@mode/shared/util-js';
import { getFormatMapKeyByField } from 'libs/charts/util-vegas/src/lib/field-format-helpers';
import { ChartFacade } from '@mode/charts/contract';
import { BehaviorSubject, debounce, EMPTY, Observable, Subject, takeUntil, timer } from 'rxjs';

export interface BinningModalData {
  targetField: CompiledField;
  vizToken: string;
  fieldFormatMap: ChartTypes.FieldFormatMap;
  onApplyFieldBinSize: (binSize: number, field: CompiledField) => void;
}

@Component({
  standalone: true,
  imports: [CommonModule, DialogHeaderComponent, DragDropModule, IconComponent, ButtonComponent, LoaderComponent],
  selector: 'mode-binning-modal',
  templateUrl: './binning-modal.component.html',
  styleUrls: ['./binning-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BinningModalComponent implements OnInit, OnDestroy {
  readonly targetField: CompiledField;
  readonly fieldFormatMap: ChartTypes.FieldFormatMap;
  readonly vizToken: string;
  readonly refId = 'bin-settings-dialog';
  readonly onApplyFieldBinSize: (binSize: number, field: CompiledField) => void;

  currentBinSizeVal: string | undefined;
  suggestedBinSize: string | undefined;
  errorMessage: string | undefined;
  binSizetoFieldMap: BinSizeTypes.BinSizeToFieldMap | undefined;
  enableUseRecommended = true;

  private isLoadingSubject$ = new BehaviorSubject<boolean>(true);
  private destroyed$ = new Subject<void>();

  isLoading$: Observable<boolean>;
  showLoader$: Observable<boolean>;

  constructor(
    private dialogRef: MatLegacyDialogRef<BinningModalComponent>,
    @Inject(MAT_LEGACY_DIALOG_DATA) private data: BinningModalData,
    private chartFacade: ChartFacade
  ) {
    this.targetField = data.targetField;
    this.fieldFormatMap = data.fieldFormatMap;
    this.vizToken = data.vizToken;

    this.currentBinSizeVal =
      this.targetField.isBinned && typeof this.targetField.binSize === 'number'
        ? String(this.targetField.binSize)
        : String(this.suggestedBinSize);

    this.chartFacade
      .createBinSizeToFieldMapFromVizToken(this.refId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((binSizeToFieldMap: BinSizeTypes.BinSizeToFieldMap) => {
        this.binSizetoFieldMap = binSizeToFieldMap;
        this.updateSuggestedBinSize();
      });

    this.onApplyFieldBinSize = data.onApplyFieldBinSize;
    this.isLoading$ = this.isLoadingSubject$.asObservable().pipe(takeUntil(this.destroyed$));
    this.showLoader$ = this.isLoadingSubject$.asObservable().pipe(
      takeUntil(this.destroyed$),
      debounce((showLoader) => (showLoader ? timer(350) : EMPTY))
    );
  }

  ngOnInit(): void {
    if (!this.targetField.attrName) {
      return;
    }

    if (!this.binSizetoFieldMap?.has(this.targetField.attrName)) {
      this.isLoadingSubject$.next(true);
      this.chartFacade.getBinSize(
        this.vizToken,
        this.targetField.attrName,
        `${FLAMINGO_RESERVED_REFS.BinSize}#${this.refId}`
      );
    }

    this.chartFacade
      .createErrorFromDomainQuery(`${FLAMINGO_RESERVED_REFS.BinSize}#${this.refId}`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((error) => this.onLoadError(error));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onBinSizeInputChange(binsize: number) {
    this.currentBinSizeVal = String(binsize);
    this.checkInputErrors();
  }

  onClickUseRecommended() {
    this.currentBinSizeVal = String(this.suggestedBinSize);
  }

  formatBinSize(binSize: number) {
    const formatKey = getFormatMapKeyByField(this.targetField.toEncField());

    const axisFormat = this.fieldFormatMap?.[formatKey]?.[ChartTypes.FieldFormatTypes.Axis];
    const scaleFormat = axisFormat?.[ChartTypes.FieldAxisTypes.Scale] ?? ({} as ChartTypes.ScaleFormat);
    const scaleFormatConfig = scaleFormat[ChartTypes.FieldAxisTypes.Default];

    const headerFormat = this.fieldFormatMap?.[formatKey]?.[ChartTypes.FieldFormatTypes.Header];
    const headerDefaultFormatConfig = headerFormat?.[ChartTypes.FieldHeaderTypes.Default];

    const formatConfig = scaleFormatConfig ?? headerDefaultFormatConfig;

    const formattedValue = formatConfig
      ? getFormatterByType(formatConfig.type)(binSize, formatConfig)
      : String(binSize);

    return formattedValue;
  }

  close() {
    this.dialogRef.close();
  }

  doApply(close: boolean) {
    if (this.checkInputErrors()) {
      return;
    }

    const binsize = Number(this.currentBinSizeVal);
    this.onApplyFieldBinSize(binsize, this.targetField);

    if (close) {
      this.close();
    }
  }

  checkInputErrors(): boolean {
    let errorMessage: string | undefined = undefined;

    const binSize = Number(this.currentBinSizeVal);
    if (Number.isNaN(binSize) || binSize <= 0) {
      errorMessage = 'Bin size must be a number greater than zero';
    }

    this.errorMessage = errorMessage;

    return !!errorMessage;
  }

  updateSuggestedBinSize() {
    if (this.targetField.attrName && this.binSizetoFieldMap?.has(this.targetField.attrName)) {
      const binSize = this.binSizetoFieldMap.get(this.targetField.attrName)?.binSize ?? 1;
      this.suggestedBinSize = this.formatBinSize(binSize);
      this.isLoadingSubject$.next(false);
    }
  }

  onLoadError(error: FlamingoResponse.FlamingoErrorResponse | undefined) {
    if (error !== undefined) {
      // Clear the loading indicator, but don't set an error state.
      // setting suggested bin size to 1 as fallback
      this.isLoadingSubject$.next(false);
      console.warn(`Unable to load suggested bin size. Error: ${JSON.stringify(error)}`);
      this.suggestedBinSize = undefined;
      this.enableUseRecommended = false;
    }
  }
}
